import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'
import { LAYERS } from 'utils/layers'

const STATUSES = [
  { label: 'Satisfied', colour: RAG_LEVELS.low.fg, text: 'current evidence is on file. A valid document, or a completed task within its cadence. These are the obligations that count towards your score.' },
  { label: 'In review', colour: RAG_LEVELS.medium.fg, text: 'evidence has been submitted but not yet approved by a reviewer. It doesn’t count towards your score until it’s approved.' },
  { label: 'Pending',   colour: RAG_LEVELS.medium.fg, text: 'we’re still awaiting the first piece of evidence. The obligation applies, but nothing has been uploaded or completed yet.' },
  { label: 'Overdue',   colour: RAG_LEVELS.critical.fg, text: 'evidence existed but has lapsed. A document has expired, or a recurring task is past its due date plus any grace period.' },
  { label: 'Skipped',   colour: RAG_LEVELS.none.fg, text: 'marked not applicable to this Building Passport (for example, no gas supply or no lift). Skipped obligations are removed from the calculation entirely.' },
]

const CRITICALITY = [
  { label: 'Low',      colour: RAG_LEVELS.low.fg },
  { label: 'Medium',   colour: RAG_LEVELS.medium.fg },
  { label: 'High',     colour: RAG_LEVELS.high.fg },
  { label: 'Critical', colour: RAG_LEVELS.critical.fg },
]

const PENALTY = [
  { label: 'No specific penalty', text: 'guidance or good practice with no statutory penalty.' },
  { label: 'Civil penalty', text: 'a financial penalty from the authority. For example MEES, or an electrical-safety breach in rented housing.' },
  { label: 'Fine', text: 'a criminal fine on conviction. For example a Building Regulations breach.' },
  { label: 'Unlimited fine', text: 'an unlimited criminal fine. For example the waste duty of care, or mandatory occurrence reporting.' },
  { label: 'Imprisonment', text: 'a criminal offence carrying imprisonment plus an unlimited fine. Fire safety, gas, asbestos, lifting, legionella and Building Safety Act duties.' },
]

const ComplianceScoreExplainer = ({ align = 'right' }) => {
  const [open, setOpen] = useState(false)
  const btnRef = useRef(null)
  const popRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = e => {
      if (
        popRef.current && !popRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false)
    }
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const [coords, setCoords] = useState(null)
  useEffect(() => {
    if (open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect()
      const margin = 12
      const popWidth = Math.min(360, window.innerWidth - 2 * margin)
      const desiredLeft = align === 'left' ? r.left : r.right - popWidth
      const left = Math.max(margin, Math.min(desiredLeft, window.innerWidth - popWidth - margin))
      setCoords({ top: r.bottom + 8, left })
    }
  }, [open, align])

  return (
    <Wrap>
      <InfoButton
        ref={btnRef}
        type='button'
        aria-label='How is the compliance score calculated?'
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        data-tour-id='compliance.score-explainer'
      >
        <InfoOutlinedIcon style={{ fontSize: 15 }} />
        <span>How is this calculated?</span>
      </InfoButton>

      {open && coords && createPortal(
        <Pop
          ref={popRef}
          role='dialog'
          aria-label='How the compliance score is calculated'
          style={{ top: coords.top, left: coords.left }}
          data-tour-id='compliance.score-explainer-popover'
        >
          <PopTitle>How the compliance score is calculated</PopTitle>
          <PopLead>
            It’s a <strong>risk-weighted</strong> score: the share of your
            applicable obligations that are satisfied, where each one is weighted
            by the consequence of getting it wrong. A serious statutory duty in
            the top penalty tier counts far more than a low-risk, no-penalty one,
            so the score reflects real exposure rather than a simple tick count.
            Obligations marked not applicable are left out.
          </PopLead>

          <Formula>
            <FormulaTop>Σ risk weight of satisfied obligations</FormulaTop>
            <FormulaBar />
            <FormulaBottom>Σ risk weight of applicable obligations</FormulaBottom>
            <FormulaTimes>× 100</FormulaTimes>
          </Formula>

          <SectionTitle>How each obligation is weighted</SectionTitle>
          <PopLead>
            Each penalty tier carries a number from 1 (no specific penalty) to 5
            (imprisonment), and each criticality level a number from 1 (low) to 4
            (critical). An obligation’s risk weight is the{' '}
            <strong>penalty number squared, multiplied by the criticality
            number</strong>, so weights run from 1 up to 100. Squaring the penalty
            makes the legal consequence lead: a critical duty carrying
            imprisonment weighs 5² × 4 = 100, while a critical duty with only a
            civil penalty weighs 2² × 4 = 16.
          </PopLead>

          <SectionTitle>Per-unit obligations</SectionTitle>
          <PopLead>
            Some obligations apply to every unit rather than the whole building,
            such as an electrical certificate for each flat. These earn{' '}
            <strong>partial credit in proportion to coverage</strong>: with 7 of
            10 units holding current evidence, the obligation contributes 70%
            of its risk weight to the score.
          </PopLead>
          <PopLead>
            How a unit starts counting depends on the obligation type. A per-unit{' '}
            <strong>task</strong> counts as soon as that unit’s task is completed,
            because a completed task is its own evidence. A per-unit{' '}
            <strong>document</strong> counts only once a reviewer has approved that
            unit’s file; <strong>approving one unit does not approve the others</strong>,
            each is checked on its own, and if a document changes it returns to
            review. Expired unit documents never count.
          </PopLead>

          <SectionTitle>What each status means</SectionTitle>
          <StatusList>
            {STATUSES.map(s => (
              <StatusRow key={s.label}>
                <Dot $colour={s.colour} />
                <StatusText><strong>{s.label}</strong>: {s.text}</StatusText>
              </StatusRow>
            ))}
          </StatusList>

          <SectionTitle>Criticality (life-safety)</SectionTitle>
          <CritRow>
            {CRITICALITY.map(c => (
              <CritChip key={c.label} $colour={c.colour}>{c.label}</CritChip>
            ))}
          </CritRow>
          <FootNote>
            Criticality normally comes from the regulatory default, but a building manager can
            override it for a specific building. When they do, this score uses the overridden
            level instead of the default.
          </FootNote>

          <SectionTitle>Penalty severity (legal consequence)</SectionTitle>
          <StatusList>
            {PENALTY.map(p => (
              <StatusRow key={p.label}>
                <StatusText><strong>{p.label}</strong>: {p.text}</StatusText>
              </StatusRow>
            ))}
          </StatusList>
          <FootNote>
            A Building Passport with no applicable obligations shows "No score" rather
            than 0%, so an empty programme is never mistaken for a failing one.
          </FootNote>
        </Pop>,
        document.body
      )}
    </Wrap>
  )
}

export default ComplianceScoreExplainer

const Wrap = styled.span`
  display: inline-flex;
`

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(12, 18, 41, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 4px 11px 4px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease;
  &:hover { background: rgba(12, 18, 41, 0.06); }
`

const Pop = styled.div`
  position: fixed;
  z-index: ${LAYERS.popover};
  width: 360px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 96px);
  overflow-y: auto;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 14px;
  padding: 18px 20px;
  box-shadow: 0 18px 44px rgba(12, 18, 41, 0.22);
`

const PopTitle = styled.h4`
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const PopLead = styled.p`
  margin: 0 0 12px;
  font-size: 12.5px;
  line-height: 1.5;
  color: #4d4d56;
`

const Formula = styled.div`
  display: inline-flex; flex-direction: column; align-items: center;
  position: relative;
  margin: 0 0 16px;
  padding: 10px 14px;
  background: ${({ theme }) => theme.colors.lighterGrey};
  border-radius: 10px;
`

const FormulaTop = styled.span`
  font-size: 12px; font-weight: 700; color: #1f8c97;
`

const FormulaBar = styled.span`
  display: block; width: 100%; height: 1px;
  background: #2a2a35; margin: 4px 0;
`

const FormulaBottom = styled.span`
  font-size: 12px; font-weight: 700; color: ${({ theme }) => theme.colors.haiti};
`

const FormulaTimes = styled.span`
  position: absolute; right: -42px; top: 50%; transform: translateY(-50%);
  font-size: 12px; font-weight: 700; color: ${({ theme }) => theme.colors.grey[800]};
`

const SectionTitle = styled.h5`
  margin: 0 0 8px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const StatusList = styled.div`
  display: flex; flex-direction: column; gap: 8px;
  margin-bottom: 16px;
`

const StatusRow = styled.div`
  display: flex; gap: 8px; align-items: flex-start;
`

const Dot = styled.span`
  flex: 0 0 auto;
  width: 9px; height: 9px; margin-top: 4px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
  box-shadow: 0 0 0 2px ${({ $colour }) => `${$colour}33`};
`

const StatusText = styled.span`
  font-size: 12px; line-height: 1.45; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const CritRow = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px;
`

const CritChip = styled.span`
  display: inline-flex; align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${({ $colour }) => `${$colour}14`};
  color: ${({ $colour }) => $colour};
  border: 1px solid ${({ $colour }) => `${$colour}33`};
  font-size: 11px; font-weight: 800;
`

const FootNote = styled.p`
  margin: 0;
  font-size: 11.5px;
  line-height: 1.45;
  font-style: italic;
  color: #6e6e78;
`
